/**
 * Working out what a downloaded document actually is.
 *
 * The document service never stores a content type, so every download arrives as
 * application/octet-stream. The file name is the usual fallback, but documents addressed by key
 * alone -- collateral evidence, anything labelled rather than named -- have no name to read
 * either, so the last resort is the content itself.
 *
 * Shared by the application workspace and the document view so the two cannot drift apart.
 */

export type DocumentKind = 'pdf' | 'image' | 'other';

export interface SniffedType {
  kind: DocumentKind;
  mediaType: string;
  /** Includes the leading dot, or empty when the content is unrecognised. */
  extension: string;
}

/** Classifies by file name or declared content type. Cheap, and right most of the time. */
export const documentKind = (name?: string, contentType?: string): DocumentKind => {
  const type = (contentType || '').toLowerCase();
  const fileName = (name || '').toLowerCase();
  if (type.includes('pdf') || fileName.endsWith('.pdf')) return 'pdf';
  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|tiff?)$/.test(fileName)) return 'image';
  return 'other';
};

/** Classifies by the file's magic bytes, for when neither the name nor the header says anything. */
export const sniffBlob = async (blob: Blob): Promise<SniffedType> => {
  try {
    const bytes = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    const startsWith = (...signature: number[]) => signature.every((byte, i) => bytes[i] === byte);

    if (startsWith(0x25, 0x50, 0x44, 0x46)) return { kind: 'pdf', mediaType: 'application/pdf', extension: '.pdf' };
    if (startsWith(0x89, 0x50, 0x4e, 0x47)) return { kind: 'image', mediaType: 'image/png', extension: '.png' };
    if (startsWith(0xff, 0xd8, 0xff)) return { kind: 'image', mediaType: 'image/jpeg', extension: '.jpg' };
    if (startsWith(0x47, 0x49, 0x46, 0x38)) return { kind: 'image', mediaType: 'image/gif', extension: '.gif' };
    if (startsWith(0x52, 0x49, 0x46, 0x46) && bytes[8] === 0x57 && bytes[9] === 0x45) {
      return { kind: 'image', mediaType: 'image/webp', extension: '.webp' };
    }
  } catch {
    // A blob we cannot read is one we cannot classify; fall through.
  }
  return { kind: 'other', mediaType: blob.type || 'application/octet-stream', extension: '' };
};

/**
 * Resolves a blob to something a browser will render, falling back to the content when the name
 * and header are silent. Returns the kind and a blob typed to match.
 */
export const resolveForPreview = async (
  blob: Blob,
  name?: string,
  contentType?: string
): Promise<{ kind: DocumentKind; blob: Blob }> => {
  let kind = documentKind(name, contentType || blob.type);
  let mediaType = kind === 'pdf' ? 'application/pdf' : blob.type;

  if (kind === 'other') {
    const sniffed = await sniffBlob(blob);
    kind = sniffed.kind;
    mediaType = sniffed.mediaType;
  }

  const typed = mediaType && mediaType !== blob.type ? new Blob([blob], { type: mediaType }) : blob;
  return { kind, blob: typed };
};

/** Adds an extension taken from the content when the given name has none. */
export const withExtension = async (fileName: string, blob: Blob): Promise<string> => {
  if (/\.[a-z0-9]{2,5}$/i.test(fileName)) return fileName;
  return fileName + (await sniffBlob(blob)).extension;
};
